import {
  LocationInput,
  MediaData,
  MediaPickerComponent,
  MediaType,
  messageFormatMarkers,
  PickerMode,
  GeoJsonType,
  Severity,
  useShareLocation,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Pressable,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { Menu } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { severityToVariant, TypeIcon } from '../crop-circle/Components/Type';
import {
  ArrowUpIcon,
  BoldIcon,
  BulletListIcon,
  DocumentIcon,
  ImageIcon,
  ItalicIcon,
  LinkIcon,
  LocationIcon,
  OrderedListIcon,
  StrikethroughIcon,
  TextIcon,
  UnderlineIcon,
} from '../crop-circle/icons';
import { Icon } from '../crop-circle/icons';
import { spacing, tokens } from '../crop-circle/tokens';
import { ActionMenuList } from '../issue/messageActionMenu';
import { RenderMediaItem } from '../media/renderMediaItem';
import { MentionInput } from '../mentionInputField';
import { useMentionTextFormatting } from '../mentionInputField/useMentionTextFormatting';

/**
 * The send-as menu, and **the only place a human-readable label meets a
 * `Severity`**. `ReportIssueInput.severity` is `String!` server-side while
 * `Issue.severity` reads back as an enum, so a label that reached the wire
 * would write a document every later read fails on (gripp-9ee). Keeping the
 * pairing in one table, and passing the `Severity` member, never the label, is
 * what makes that unrepresentable.
 */
const SEND_AS_OPTIONS: { severity: Severity; labelKey: string }[] = [
  { severity: Severity.Red, labelKey: 'issue.types.urgent' },
  { severity: Severity.Yellow, labelKey: 'issue.types.pending' },
  { severity: Severity.Green, labelKey: 'issue.types.solved' },
];

const MIN_CARD_HEIGHT = 124;

/** Reference geometry: 16px glyphs in 20px boxes, 24px apart. */
const GLYPH_BOX = 20;
const GLYPH_GAP = spacing['2xl'];

export type ChatComposerSendAs = {
  severity?: Severity;
  onChange: (severity?: Severity) => void;
  /** The activity's own type, left out of the menu: it would change nothing. */
  current?: Severity;
};

export type ChatComposerCardProps = {
  message: string;
  onMessageChange: (message: string) => void;
  onSend: () => void;
  canSend: boolean;
  placeholder: string;
  /** Scopes the mention picker. Omit for an activity that does not exist yet. */
  issueId?: string;
  media: MediaData[];
  onMediaAdded: (media: MediaData) => void;
  onMediaDeleted: (mediaId: string) => void;
  onUploadProgress: (mediaId: string, progress: number) => void;
  /** Each picker reports under its own source, so overlapping uploads add up. */
  onUploadingChange: (source: string, busy: boolean) => void;
  /** Platform media picker; the host supplies web's or native's. */
  mediaPicker: MediaPickerComponent;
  workspaceId?: string;
  acceptedMediaTypes?: MediaType[];
  onLocationPress: () => void;
  /** A location held beside the draft rather than written into its text. */
  attachedLocation?: { onClear: () => void };
  /** Omit where a post cannot be given a type; the send control is then whole. */
  sendAs?: ChatComposerSendAs;
  containerStyle?: StyleProp<ViewStyle>;
  testID?: string;
};

/**
 * Asks for a fresh fix and reports a refusal, returning undefined when there is
 * no location to use. Takes a FRESH fix rather than reading
 * `useCurrentLocation`'s cache: that cache is written by a location *watcher*,
 * which never runs on web, so the cached read resolved undefined every time.
 * Sharing a location is user-initiated, so asking for the permission at the
 * moment of the tap is also the honest place to ask.
 */
export const useComposerLocation = () => {
  const { t } = useTranslation();
  const { shareLocation } = useShareLocation();

  return async () => {
    const result = await shareLocation();

    if (result.status === 'denied') {
      Toast.show({
        type: 'error',
        text1: t('issue.location.deniedTitle'),
        text2: t('issue.location.deniedMessage'),
      });
      return undefined;
    }

    if (result.status === 'unavailable') {
      Toast.show({
        type: 'error',
        text1: t('issue.location.unavailableTitle'),
        text2: t('issue.location.unavailableMessage'),
      });
      return undefined;
    }

    // GeoJSON order is [longitude, latitude]: the reverse of how the fix
    // reports it, and of how anyone says it out loud.
    const location: LocationInput = {
      type: GeoJsonType.Point,
      coordinates: [result.longitude, result.latitude],
    };
    return { location, url: result.url };
  };
};

/**
 * The chat composer's card: input on top, a toolbar row beneath it, a split send
 * control at the bottom right. Holds only what the card itself shows; the draft
 * and how it posts belong to the host, which is what lets the asset chat (which
 * files a new activity) and an activity's own chat (which adds a message) share
 * one composer.
 *
 * `MentionInput` handles text and mentions, and `useMentionTextFormatting` drives
 * the text-style row with the same markers `issueFooterMobile` writes, so a
 * message composed here is byte-identical to one composed there.
 *
 * The bottom bar has two modes that crossfade in one zone, per the reference:
 * *attach* (add image, add document, share location) and *text* (the style
 * markers). Dictation and Ask Gripp AI are drawn in the reference but are out of
 * scope for v1, so their glyphs are omitted rather than shown dead.
 */
export const ChatComposerCard = ({
  message,
  onMessageChange,
  onSend,
  canSend,
  placeholder,
  issueId,
  media,
  onMediaAdded,
  onMediaDeleted,
  onUploadProgress,
  onUploadingChange,
  mediaPicker,
  workspaceId,
  acceptedMediaTypes = [MediaType.Image, MediaType.Video, MediaType.Document],
  onLocationPress,
  attachedLocation,
  sendAs,
  containerStyle,
  testID,
}: ChatComposerCardProps) => {
  const { t } = useTranslation();
  const MediaPicker = mediaPicker;

  const [menuVisible, setMenuVisible] = useState(false);
  const [isTextMode, setIsTextMode] = useState(false);
  const [inputHeight, setInputHeight] = useState(24);

  const imagePickerRef = useRef<(() => void) | null>(null);
  const documentPickerRef = useRef<(() => void) | null>(null);

  const imageTypes = acceptedMediaTypes.filter(
    (type) => type === MediaType.Image || type === MediaType.Video
  );
  const acceptsDocuments = acceptedMediaTypes.includes(MediaType.Document);

  const {
    selection,
    onSelectionChange,
    wrapSelection,
    insertLink,
    prefixLines,
  } = useMentionTextFormatting({ value: message, onChange: onMessageChange });

  // The pill's fill changes with `canSend`, so its glyphs change with it: white
  // on brand orange in both modes, the inverse pair at rest.
  const sendGlyphColor = canSend
    ? Colors.role.textOnBrand
    : Colors.role.textOnInverse;

  const sendAsOption = SEND_AS_OPTIONS.find(
    (option) => option.severity === sendAs?.severity
  );

  const attachButtons: {
    label: string;
    icon: ReactNode;
    onPress: () => void;
    testID?: string;
  }[] = [
    ...(imageTypes.length
      ? [
          {
            label: t('chat.composer.addImage'),
            icon: <ImageIcon size={16} color={Colors.role.textSecondary} />,
            onPress: () => imagePickerRef.current?.(),
            testID: testID ? `${testID}-add-image` : undefined,
          },
        ]
      : []),
    ...(acceptsDocuments
      ? [
          {
            label: t('chat.composer.addDocument'),
            icon: <DocumentIcon size={16} color={Colors.role.textSecondary} />,
            onPress: () => documentPickerRef.current?.(),
            testID: testID ? `${testID}-add-document` : undefined,
          },
        ]
      : []),
    {
      label: attachedLocation
        ? t('chat.composer.removeLocation')
        : t('chat.composer.shareLocation'),
      icon: (
        <LocationIcon
          size={16}
          color={
            attachedLocation
              ? Colors.role.brandOnSurface
              : Colors.role.textSecondary
          }
        />
      ),
      onPress: onLocationPress,
      testID: testID ? `${testID}-share-location` : undefined,
    },
  ];

  // Same order and markers as issueFooterMobile's row: the point is that both
  // surfaces author one syntax, not two.
  const textButtons: { label: string; icon: ReactNode; onPress: () => void }[] =
    [
      {
        label: t('issue.footer.bold'),
        icon: <BoldIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.bold.open,
            messageFormatMarkers.bold.close
          ),
      },
      {
        label: t('issue.footer.italic'),
        icon: <ItalicIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.italic.open,
            messageFormatMarkers.italic.close
          ),
      },
      {
        label: t('issue.footer.underline'),
        icon: <UnderlineIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.underline.open,
            messageFormatMarkers.underline.close
          ),
      },
      {
        label: t('issue.footer.strikethrough'),
        icon: <StrikethroughIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () =>
          wrapSelection(
            messageFormatMarkers.strikethrough.open,
            messageFormatMarkers.strikethrough.close
          ),
      },
      {
        label: t('issue.footer.link'),
        icon: <LinkIcon size={16} color={Colors.role.textSecondary} />,
        onPress: insertLink,
      },
      {
        label: t('issue.footer.orderedList'),
        icon: <OrderedListIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () => prefixLines((index) => `${index + 1}. `),
      },
      {
        label: t('issue.footer.bulletList'),
        icon: <BulletListIcon size={16} color={Colors.role.textSecondary} />,
        onPress: () => prefixLines(() => '- '),
      },
    ];

  return (
    <View style={[styles.wrapper, containerStyle]} testID={testID}>
      <View style={[styles.card, { minHeight: MIN_CARD_HEIGHT }]}>
        {(sendAsOption || attachedLocation) && (
          <View style={styles.chipRow}>
            {sendAsOption && (
              <View
                style={styles.chip}
                testID={testID ? `${testID}-send-as-chip` : undefined}
              >
                <TypeIcon
                  variant={severityToVariant[sendAsOption.severity]}
                  size={12}
                />
                <Text style={styles.chipText} numberOfLines={1}>
                  {t('chat.composer.postingAs', {
                    type: t(sendAsOption.labelKey),
                  })}
                </Text>
                <Pressable
                  onPress={() => sendAs?.onChange(undefined)}
                  accessibilityRole="button"
                  accessibilityLabel={t('chat.composer.postAsMessageInstead')}
                  testID={testID ? `${testID}-clear-send-as` : undefined}
                >
                  <Icon name="x" size={12} color={Colors.role.textSoft} />
                </Pressable>
              </View>
            )}
            {attachedLocation && (
              <View
                style={styles.chip}
                testID={testID ? `${testID}-location-chip` : undefined}
              >
                <LocationIcon size={12} color={Colors.role.textSoft} />
                <Text style={styles.chipText} numberOfLines={1}>
                  {t('chat.composer.locationAttached')}
                </Text>
                <Pressable
                  onPress={attachedLocation.onClear}
                  accessibilityRole="button"
                  accessibilityLabel={t('chat.composer.removeLocation')}
                  testID={testID ? `${testID}-clear-location` : undefined}
                >
                  <Icon name="x" size={12} color={Colors.role.textSoft} />
                </Pressable>
              </View>
            )}
          </View>
        )}

        <View style={styles.inputArea}>
          <MentionInput
            inputFieldText={message}
            setInputFieldText={onMessageChange}
            setIsMultiline={() => undefined}
            inputHeight={inputHeight}
            setInputHeight={setInputHeight}
            send={onSend}
            placeholder={placeholder}
            issueId={issueId}
            suggestionsPosition="top"
            flush
            onSelectionChange={onSelectionChange}
            selection={selection}
          />
        </View>

        {media.length > 0 && (
          <View
            style={styles.mediaRow}
            testID={testID ? `${testID}-media` : undefined}
          >
            {media.map((item) => (
              <RenderMediaItem
                key={item.id}
                mediaItem={item}
                deleteMediaItem={onMediaDeleted}
                canDelete
                displayWidth={72}
              />
            ))}
          </View>
        )}

        <View style={styles.bottomBar}>
          <View style={styles.modeZone}>
            {/* Outside the crossfade: it is the switch between the two modes,
                not a member of either. */}
            <Pressable
              onPress={() => setIsTextMode((current) => !current)}
              style={styles.glyph}
              accessibilityRole="button"
              accessibilityState={{ selected: isTextMode }}
              accessibilityLabel={t('issue.footer.textStyles')}
              testID={testID ? `${testID}-text-mode` : undefined}
            >
              <TextIcon
                size={16}
                color={
                  isTextMode
                    ? Colors.role.brandOnSurface
                    : Colors.role.textSecondary
                }
              />
            </Pressable>

            <CrossfadeGroup
              active={!isTextMode}
              from="left"
              gap={GLYPH_GAP}
              testID={testID ? `${testID}-attach-bar` : undefined}
            >
              {attachButtons.map((button) => (
                <Pressable
                  key={button.testID ?? button.label}
                  onPress={button.onPress}
                  style={styles.glyph}
                  accessibilityRole="button"
                  accessibilityLabel={button.label}
                  // Both groups stay mounted for the crossfade, so the hidden
                  // one has to leave the tab order too: opacity 0 and
                  // pointerEvents alone still let a keyboard reach it.
                  focusable={!isTextMode}
                  testID={button.testID}
                >
                  {button.icon}
                </Pressable>
              ))}
            </CrossfadeGroup>

            <CrossfadeGroup
              active={isTextMode}
              from="right"
              overlay
              gap={spacing.md}
              testID={testID ? `${testID}-text-bar` : undefined}
            >
              {textButtons.map((button) => (
                <Pressable
                  key={button.label}
                  onPress={button.onPress}
                  style={styles.glyph}
                  accessibilityRole="button"
                  accessibilityLabel={button.label}
                  focusable={isTextMode}
                >
                  {button.icon}
                </Pressable>
              ))}
            </CrossfadeGroup>
          </View>

          {/* One pill, both halves: dark at rest, brand once there is
              something to send. */}
          <View style={[styles.sendPill, canSend && styles.sendPillActive]}>
            {sendAs && (
              <>
                <Menu
                  visible={menuVisible}
                  onDismiss={() => setMenuVisible(false)}
                  contentStyle={styles.menu}
                  anchor={
                    <Pressable
                      onPress={() => setMenuVisible(true)}
                      style={styles.sendHalf}
                      accessibilityRole="button"
                      accessibilityLabel={t('chat.composer.sendAs')}
                      testID={testID ? `${testID}-send-as` : undefined}
                    >
                      <Icon
                        name={menuVisible ? 'Chevron Up' : 'Chevron Down'}
                        size={16}
                        color={sendGlyphColor}
                      />
                    </Pressable>
                  }
                >
                  <ActionMenuList>
                    {/* Only the way back from a type: in the default mode it would
                      be a row that does nothing. */}
                    {sendAs.severity && (
                      <SendAsRow
                        label={t('chat.composer.asMessage')}
                        onPress={() => {
                          setMenuVisible(false);
                          sendAs.onChange(undefined);
                        }}
                        testID={
                          testID ? `${testID}-send-as-message` : undefined
                        }
                      />
                    )}
                    {SEND_AS_OPTIONS.filter(
                      (option) => option.severity !== sendAs.current
                    ).map((option) => (
                      <SendAsRow
                        key={option.severity}
                        label={t(option.labelKey)}
                        severity={option.severity}
                        isSelected={option.severity === sendAs.severity}
                        onPress={() => {
                          setMenuVisible(false);
                          sendAs.onChange(option.severity);
                        }}
                        testID={
                          testID
                            ? `${testID}-send-as-${option.severity}`
                            : undefined
                        }
                      />
                    ))}
                  </ActionMenuList>
                </Menu>

                <View
                  style={[styles.sendSeam, canSend && styles.sendSeamActive]}
                />
              </>
            )}

            <Pressable
              onPress={onSend}
              disabled={!canSend}
              style={styles.sendHalf}
              accessibilityRole="button"
              accessibilityState={{ disabled: !canSend }}
              accessibilityLabel={
                sendAsOption
                  ? t('chat.composer.sendAsType', {
                      type: t(sendAsOption.labelKey),
                    })
                  : t('chat.composer.send')
              }
              testID={testID ? `${testID}-send` : undefined}
            >
              <ArrowUpIcon size={18} color={sendGlyphColor} />
            </Pressable>
          </View>
        </View>
      </View>

      {/* Mounted offscreen purely to hand back their openers: the visible
          controls are the glyphs above, so the picker's own trigger (and its
          negative margins) never render. */}
      <View style={styles.headlessPicker} pointerEvents="none">
        {imageTypes.length > 0 && (
          <MediaPicker
            mode={PickerMode.PICKER}
            acceptedMediaTypes={imageTypes}
            setMedia={onMediaAdded}
            onUploadError={onMediaDeleted}
            setProgress={onUploadProgress}
            setUploading={(busy) => onUploadingChange('image', busy)}
            workspaceId={workspaceId}
            onPickerReady={(opener) => {
              imagePickerRef.current = opener;
            }}
          />
        )}
        {acceptsDocuments && (
          <MediaPicker
            mode={PickerMode.ATTACHMENT}
            acceptedMediaTypes={[MediaType.Document]}
            setMedia={onMediaAdded}
            onUploadError={onMediaDeleted}
            setProgress={onUploadProgress}
            setUploading={(busy) => onUploadingChange('document', busy)}
            workspaceId={workspaceId}
            onPickerReady={(opener) => {
              documentPickerRef.current = opener;
            }}
          />
        )}
      </View>
    </View>
  );
};

/**
 * One half of the bottom bar's two modes. The attach group stays in flow so it
 * reserves the row's width; the text group overlays it, so the two swap in
 * place instead of the bar resizing under the cursor.
 */
const CrossfadeGroup = ({
  active,
  from,
  overlay,
  gap,
  children,
  testID,
}: {
  active: boolean;
  from: 'left' | 'right';
  overlay?: boolean;
  /** Attach sits at 24, the denser text row at 12: both from the reference. */
  gap: number;
  children: ReactNode;
  testID?: string;
}) => {
  const progress = useRef(new Animated.Value(active ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: active ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [active, progress]);

  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [from === 'left' ? -8 : 8, 0],
  });

  return (
    <Animated.View
      style={[
        styles.glyphGroup,
        overlay && styles.glyphGroupOverlay,
        { gap, opacity: progress, transform: [{ translateX }] },
      ]}
      pointerEvents={active ? 'auto' : 'none'}
      // The inactive mode is invisible but still mounted, so it is hidden
      // from assistive technology as well as from the pointer. Its buttons
      // additionally drop out of the tab order (see `focusable` on each).
      accessibilityElementsHidden={!active}
      importantForAccessibility={active ? 'auto' : 'no-hide-descendants'}
      testID={testID}
    >
      {children}
    </Animated.View>
  );
};

/**
 * One row of the send-as menu. Mirrors `messageActionMenu`'s rows rather than
 * Paper's `Menu.Item`, which reserves a wide fixed icon column.
 */
const SendAsRow = ({
  label,
  severity,
  isSelected,
  onPress,
  testID,
}: {
  label: string;
  severity?: Severity;
  isSelected?: boolean;
  onPress: () => void;
  testID?: string;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: !!isSelected }}
      testID={testID}
      style={[styles.menuItem, hovered && styles.menuItemHovered]}
    >
      {severity ? (
        <TypeIcon variant={severityToVariant[severity]} size={16} />
      ) : (
        <Icon name="Comment" size={16} color={Colors.role.textStrong} />
      )}
      <Text
        style={[styles.menuItemTitle, isSelected && styles.menuItemSelected]}
      >
        {label}
      </Text>
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  wrapper: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  // The composer is a card, not a row: input on top, controls beneath.
  card: {
    backgroundColor: role.surfaceRaised,
    borderRadius: tokens.borderRadius.sm,
    padding: spacing.lg,
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
  },
  chipText: {
    ...tokens.typography.label.medium,
    color: role.textStrong,
    flexShrink: 1,
  },
  inputArea: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  mediaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  bottomBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modeZone: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    flexShrink: 1,
    // The toggle is one glyph away from the group it switches, same as the
    // spacing within the group itself.
    gap: GLYPH_GAP,
  },
  glyphGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Starts where the attach group starts, so the two modes swap in place
  // rather than sliding sideways: the toggle's width plus the zone's gap.
  glyphGroupOverlay: {
    position: 'absolute',
    left: GLYPH_BOX + GLYPH_GAP,
    top: 0,
    bottom: 0,
  },
  glyph: {
    width: GLYPH_BOX,
    height: GLYPH_BOX,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // One pill split into two halves by a hairline seam, per the reference.
  sendPill: {
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: role.surfaceInverse,
  },
  sendPillActive: {
    backgroundColor: tokens.colors.brand.core,
  },
  sendHalf: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendSeam: {
    width: 1,
    marginVertical: 6,
    backgroundColor: role.textOnInverse,
    opacity: 0.4,
  },
  sendSeamActive: {
    backgroundColor: role.textOnBrand,
  },
  menu: {
    backgroundColor: role.surfacePopover,
    borderRadius: tokens.borderRadius.md,
    paddingVertical: 0,
  },
  menuItem: {
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    backgroundColor: role.surfacePopover,
  },
  menuItemHovered: {
    backgroundColor: role.surfaceInverseSoft,
  },
  menuItemTitle: {
    ...tokens.typography.label.medium,
    color: role.textStrong,
  },
  menuItemSelected: {
    color: role.brandOnSurface,
  },
  headlessPicker: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    width: 0,
    height: 0,
    opacity: 0,
  },
}));
